import { motion } from "framer-motion";

/**
 * GoldenLantern - A levitating Victorian/Gothic lantern with breathing glow
 * Creates a mystical focal point for the welcome screen
 */
export const GoldenLantern = ({ className = "" }) => {
    return (
        <div className={`relative ${className}`}>
            {/* Outer glow - breathing effect */}
            <motion.div
                className="absolute inset-0 rounded-full blur-3xl"
                style={{
                    background: "radial-gradient(ellipse at center, rgba(255, 180, 50, 0.3) 0%, rgba(212, 175, 55, 0.1) 40%, transparent 70%)",
                    width: "300px",
                    height: "400px",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                }}
                animate={{
                    opacity: [0.4, 0.7, 0.4],
                    scale: [1, 1.15, 1],
                }}
                transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                }}
            />

            {/* Secondary ambient glow */}
            <motion.div
                className="absolute rounded-full blur-2xl"
                style={{
                    background: "radial-gradient(ellipse at center, rgba(255, 200, 100, 0.25) 0%, transparent 60%)",
                    width: "200px",
                    height: "280px",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                }}
                animate={{
                    opacity: [0.5, 0.8, 0.5],
                    scale: [1, 1.1, 1],
                }}
                transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.5,
                }}
            />

            {/* Smoke/mist particles floating around */}
            <div className="absolute inset-0 pointer-events-none">
                {[...Array(8)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute rounded-full"
                        style={{
                            width: 4 + Math.random() * 8,
                            height: 4 + Math.random() * 8,
                            background: `radial-gradient(circle, rgba(212, 175, 55, ${0.2 + Math.random() * 0.3}) 0%, transparent 70%)`,
                            left: `${30 + Math.random() * 40}%`,
                            top: `${20 + Math.random() * 60}%`,
                        }}
                        animate={{
                            y: [-20, -60 - Math.random() * 40],
                            x: [0, (Math.random() - 0.5) * 40],
                            opacity: [0, 0.8, 0],
                            scale: [0.5, 1.5, 0.5],
                        }}
                        transition={{
                            duration: 4 + Math.random() * 3,
                            repeat: Infinity,
                            delay: i * 0.5,
                            ease: "easeOut",
                        }}
                    />
                ))}
            </div>

            {/* Levitating lantern container */}
            <motion.div
                className="relative"
                animate={{
                    y: [0, -8, 0],
                }}
                transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                }}
            >
                {/* SVG Lantern */}
                <svg
                    width="120"
                    height="180"
                    viewBox="0 0 120 180"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="relative z-10"
                >
                    {/* Lantern top hook/ring */}
                    <ellipse
                        cx="60"
                        cy="12"
                        rx="12"
                        ry="6"
                        stroke="url(#goldGradient)"
                        strokeWidth="2"
                        fill="none"
                    />
                    <path
                        d="M60 18 L60 28"
                        stroke="url(#goldGradient)"
                        strokeWidth="2"
                    />
                    
                    {/* Top cap with ornate detail */}
                    <path
                        d="M35 32 L85 32 L80 28 L40 28 Z"
                        fill="url(#goldGradient)"
                    />
                    <path
                        d="M38 32 L82 32 L78 38 L42 38 Z"
                        fill="url(#darkGold)"
                    />
                    
                    {/* Decorative top ornament */}
                    <circle cx="60" cy="28" r="4" fill="url(#goldGradient)" />
                    
                    {/* Main glass body frame - Victorian style */}
                    <path
                        d="M42 38 L42 130 C42 138 50 145 60 145 C70 145 78 138 78 130 L78 38"
                        stroke="url(#goldGradient)"
                        strokeWidth="2.5"
                        fill="none"
                    />
                    
                    {/* Glass panels (4 sides suggested) */}
                    <path
                        d="M45 42 L45 126 C45 132 52 138 60 138 C68 138 75 132 75 126 L75 42"
                        fill="rgba(255, 240, 200, 0.08)"
                        stroke="rgba(212, 175, 55, 0.3)"
                        strokeWidth="0.5"
                    />
                    
                    {/* Vertical frame bars */}
                    <line x1="52" y1="38" x2="52" y2="140" stroke="url(#goldGradient)" strokeWidth="1.5" />
                    <line x1="68" y1="38" x2="68" y2="140" stroke="url(#goldGradient)" strokeWidth="1.5" />
                    
                    {/* Horizontal decorative bands */}
                    <line x1="42" y1="55" x2="78" y2="55" stroke="url(#goldGradient)" strokeWidth="1" />
                    <line x1="42" y1="115" x2="78" y2="115" stroke="url(#goldGradient)" strokeWidth="1" />
                    
                    {/* Corner filigree details */}
                    <path
                        d="M42 38 Q38 42 42 46"
                        stroke="url(#goldGradient)"
                        strokeWidth="1.5"
                        fill="none"
                    />
                    <path
                        d="M78 38 Q82 42 78 46"
                        stroke="url(#goldGradient)"
                        strokeWidth="1.5"
                        fill="none"
                    />
                    
                    {/* Bottom base */}
                    <path
                        d="M44 145 L76 145 L80 150 L40 150 Z"
                        fill="url(#darkGold)"
                    />
                    <path
                        d="M46 150 L74 150 L70 158 L50 158 Z"
                        fill="url(#goldGradient)"
                    />
                    
                    {/* Bottom decorative point */}
                    <path
                        d="M55 158 L65 158 L60 170 Z"
                        fill="url(#goldGradient)"
                    />
                    
                    {/* Inner flame */}
                    <motion.g
                        animate={{
                            scale: [1, 1.1, 0.95, 1.05, 1],
                            opacity: [0.9, 1, 0.85, 1, 0.9],
                        }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                    >
                        {/* Flame core - bright */}
                        <ellipse
                            cx="60"
                            cy="95"
                            rx="8"
                            ry="20"
                            fill="url(#flameGradient)"
                        />
                        {/* Flame inner - white hot */}
                        <ellipse
                            cx="60"
                            cy="100"
                            rx="4"
                            ry="12"
                            fill="rgba(255, 255, 240, 0.9)"
                        />
                    </motion.g>
                    
                    {/* Inner glow from flame */}
                    <ellipse
                        cx="60"
                        cy="90"
                        rx="20"
                        ry="35"
                        fill="url(#innerGlow)"
                        opacity="0.6"
                    />
                    
                    {/* Gradients definitions */}
                    <defs>
                        <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#F5D76E" />
                            <stop offset="50%" stopColor="#D4AF37" />
                            <stop offset="100%" stopColor="#AA8C2C" />
                        </linearGradient>
                        
                        <linearGradient id="darkGold" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#8B7024" />
                            <stop offset="100%" stopColor="#5C4A18" />
                        </linearGradient>
                        
                        <linearGradient id="flameGradient" x1="0%" y1="100%" x2="0%" y2="0%">
                            <stop offset="0%" stopColor="#FF6B00" />
                            <stop offset="40%" stopColor="#FFB800" />
                            <stop offset="80%" stopColor="#FFE4AA" />
                            <stop offset="100%" stopColor="#FFFDF5" />
                        </linearGradient>
                        
                        <radialGradient id="innerGlow" cx="50%" cy="50%" r="50%">
                            <stop offset="0%" stopColor="rgba(255, 200, 100, 0.4)" />
                            <stop offset="100%" stopColor="transparent" />
                        </radialGradient>
                    </defs>
                </svg>
            </motion.div>

            {/* Light rays emanating from lantern */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {[...Array(6)].map((_, i) => (
                    <motion.div
                        key={`ray-${i}`}
                        className="absolute"
                        style={{
                            width: "2px",
                            height: "60px",
                            background: "linear-gradient(to bottom, rgba(255, 200, 100, 0.3) 0%, transparent 100%)",
                            left: "50%",
                            top: "45%",
                            transformOrigin: "center top",
                            transform: `translateX(-50%) rotate(${i * 60}deg)`,
                        }}
                        animate={{
                            opacity: [0.2, 0.5, 0.2],
                            height: ["60px", "80px", "60px"],
                        }}
                        transition={{
                            duration: 3,
                            repeat: Infinity,
                            delay: i * 0.3,
                            ease: "easeInOut",
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

export default GoldenLantern;
